import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Music2 } from "lucide-react";
import { Logo } from "./Logo";
import { useSettings } from "../context/SettingsContext";
import { api } from "../lib/api";
import { normalizeUrl } from "../lib/urlUtils";

const QUICK_LINKS = [
  { label: "How It Works", to: "/how-it-works" },
  { label: "Sessions", to: "/find-a-session" },
  { label: "Photographers", to: "/photographers" },
  { label: "About", to: "/about" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

const PHOTOGRAPHER_LINKS = [
  { label: "Host a Session", to: "/photographers/join" },
  { label: "Photographer Benefits", to: "/photographers/join" },
  { label: "Pricing", to: "/photographers/join" },
  { label: "Resources", to: "/safety" },
  { label: "Login", to: "/login" },
];

export const Footer = () => {
  const { settings } = useSettings();
  const [legalDocs, setLegalDocs] = useState([]);

  useEffect(() => {
    api.get("/content/legal").then(({ data }) => setLegalDocs(data)).catch(() => {});
  }, []);

  const socialLinks = [
    { key: "instagram", url: normalizeUrl(settings.social_instagram_url), Icon: Instagram },
    { key: "facebook", url: normalizeUrl(settings.social_facebook_url), Icon: Facebook },
    { key: "youtube", url: normalizeUrl(settings.social_youtube_url), Icon: Youtube },
    { key: "tiktok", url: normalizeUrl(settings.social_tiktok_url), Icon: Music2 },
  ].filter((s) => s.url);

  return (
    <footer className="bg-true-black text-clean-white pt-16 pb-8" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 pb-14">
          <div>
            <Logo className="h-9 mb-4" />
            <p className="text-xs font-bold tracking-[0.2em] text-clean-white/70 uppercase">
              Come alone. Leave with <span className="text-stranger-orange">a story.</span>
            </p>
          </div>

          <div>
            <h4 className="text-xs font-bold tracking-[0.2em] text-stranger-orange mb-5 uppercase">Quick Links</h4>
            <ul className="space-y-3">
              {QUICK_LINKS.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="text-sm text-clean-white/70 hover:text-clean-white transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold tracking-[0.2em] text-stranger-orange mb-5 uppercase">For Photographers</h4>
            <ul className="space-y-3">
              {PHOTOGRAPHER_LINKS.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="text-sm text-clean-white/70 hover:text-clean-white transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold tracking-[0.2em] text-stranger-orange mb-5 uppercase">Legal</h4>
            <ul className="space-y-3">
              {legalDocs.map((l) => (
                <li key={l.slug}>
                  <Link to={`/legal/${l.slug}`} className="text-sm text-clean-white/70 hover:text-clean-white transition-colors" data-testid={`footer-link-${l.slug}`}>
                    {l.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/safety" className="text-sm text-clean-white/70 hover:text-clean-white transition-colors" data-testid="footer-link-safety">
                  Safety
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 border-t border-white/10 pt-8">
          <p className="text-xs text-clean-white/50 font-medium" data-testid="footer-copyright">
            © {new Date().getFullYear()} {settings.platform_name || "Stranger Sessions"}. All Rights Reserved.
          </p>
          {socialLinks.length > 0 && (
            <div className="flex items-center gap-4" data-testid="footer-social-links">
              {socialLinks.map(({ key, url, Icon }) => (
                <a key={key} href={url} target="_blank" rel="noreferrer" data-testid={`social-${key}`} className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:border-stranger-orange hover:text-stranger-orange transition-colors">
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};
